import {
  yupArray,
  yupBoolean,
  yupEnum,
  yupNumber,
  yupObject,
  yupString,
} from 'shared/utils/YupUtils';
import { Schema } from 'yup';
import { z } from 'zod';

const reasonYupSchema = yupObject({
  type: yupString(),
  description: yupString(),
});

export const VERIFICATION_STATUS = [
  'not_submitted',
  'approved',
  'declined',
  'revoked',
  'pending',
  'reviewed',
  'non_verifiable',
] as const;

const verifiedCarrierApplicationStatusYupObjectSchema = yupObject({
  verified_carrier_application: yupObject({
    status: yupEnum(VERIFICATION_STATUS).required(),
    progress: yupObject({
      carrier_info_step_is_complete: yupBoolean().required(),
      cargo_insurance_step_is_complete: yupBoolean().required(),
      w9_step_is_complete: yupBoolean().required(),
      usdot_step_is_complete: yupBoolean().required(),
      fmcsa_phone_verification_is_complete: yupBoolean().required(),
      transporter_plate_registration_is_complete: yupBoolean().required(),
      state_motor_carrier_permit_is_complete: yupBoolean().required(),
      business_license_is_complete: yupBoolean().required(),
      is_completed: yupBoolean().required(),
      percentage: yupNumber().required(),
    }),
    declining_reason: yupArray(yupString()),
    other_declining_reason: yupString(),
    declining_reasons: yupObject({
      cargo_insurance_file_url: yupArray(yupString()).optional(),
      w9_form_url: yupArray(yupString()).optional(),
      usdot_certificate_url: yupArray(yupString()).optional(),
      reliability_score: yupArray(yupString()).optional(),
      transporter_plate_registration: yupArray(yupString()).optional(),
      state_motor_carrier_permit: yupArray(yupString()).optional(),
      business_license: yupArray(yupString()).optional(),
    }).nullable(),
    external_notes: yupObject({
      w9: yupString().optional(),
      usdot_certificate: yupString().optional(),
      cargo_insurance: yupString().optional(),
      low_reliability: yupString().optional(),
      terms_and_conditions: yupString().optional(),
      transporter_plate_registration: yupString().optional(),
      state_motor_carrier_permit: yupString().optional(),
      business_license: yupString().optional(),
    }).nullable(),
    revoking_reasons: yupObject({
      cargo_insurance_file_url: yupArray(yupString()).optional(),
      usdot_certificate_url: yupArray(yupString()).optional(),
      terms_and_conditions: yupArray(yupString()).optional(),
      fmcsa_phone: yupArray(yupString()).optional(),
    }).nullable(),
    non_verifiable_reasons: yupObject({
      business_location_is_invalid: yupArray(yupString()).optional(),
      company_type_is_invalid: yupArray(yupString()).optional(),
    }).nullable(),
    reasons: yupObject({
      w9: yupArray(reasonYupSchema).defined(),
      operation: yupArray(reasonYupSchema).defined(),
      insurance: yupArray(reasonYupSchema).defined(),
      transporter_plate_registration: yupArray(reasonYupSchema).defined(),
      state_motor_carrier_permit: yupArray(reasonYupSchema).defined(),
      business_license: yupArray(reasonYupSchema).defined(),
      terms_and_conditions: yupArray(reasonYupSchema).defined(),
      fmcsa_phone: yupArray(reasonYupSchema).defined(),
      reliability_score: yupArray(reasonYupSchema).defined(),
      company_type: yupArray(reasonYupSchema).defined(),
      business_location: yupArray(reasonYupSchema).defined(),
    }),
  }),
});

function toNullableNumber(value: unknown): unknown {
  return value == null || value === '' ? null : Number(value);
}

export type ReasonDTO = z.infer<typeof reasonSchema>;
const reasonSchema = z.object({
  type: z.string().catch(''),
  description: z.string().catch(''),
});

export type VerifiedCarrierApplicationStatusDTO = z.infer<
  typeof verifiedCarrierApplicationStatusSchema
>;
export const verifiedCarrierApplicationStatusSchema = z.object({
  verified_carrier_application: z.object({
    status: z.enum(VERIFICATION_STATUS).catch('not_submitted'),
    progress: z.object({
      carrier_info_step_is_complete: z.boolean().nullable().catch(false),
      cargo_insurance_step_is_complete: z.boolean().nullable().catch(false),
      w9_step_is_complete: z.boolean().nullable().catch(false),
      usdot_step_is_complete: z.boolean().nullable().catch(false),
      fmcsa_phone_verification_is_complete: z.boolean().nullable().catch(false),
      transporter_plate_registration_is_complete: z
        .boolean()
        .nullable()
        .catch(false),
      state_motor_carrier_permit_is_complete: z
        .boolean()
        .nullable()
        .catch(false),
      business_license_is_complete: z.boolean().nullable().catch(false),
      is_completed: z.boolean().nullable().catch(false),
      percentage: z.preprocess(
        toNullableNumber,
        z.number().nullable().catch(null),
      ),
    }),
    declining_reason: z.array(z.string().catch('')).catch([]),
    other_declining_reason: z.string().catch(''),
    declining_reasons: z
      .object({
        cargo_insurance_file_url: z.array(z.string().catch('')).catch([]),
        w9_form_url: z.array(z.string().catch('')).catch([]),
        usdot_certificate_url: z.array(z.string().catch('')).catch([]),
        reliability_score: z.array(z.string().catch('')).catch([]),
        transporter_plate_registration: z.array(z.string().catch('')).catch([]),
        state_motor_carrier_permit: z.array(z.string().catch('')).catch([]),
        business_license: z.array(z.string().catch('')).catch([]),
      })
      .nullable()
      .catch(null)
      .prefault({
        cargo_insurance_file_url: [],
        w9_form_url: [],
        usdot_certificate_url: [],
        reliability_score: [],
        transporter_plate_registration: [],
        state_motor_carrier_permit: [],
        business_license: [],
      }),
    external_notes: z
      .object({
        w9: z.string().catch(''),
        usdot_certificate: z.string().catch(''),
        cargo_insurance: z.string().catch(''),
        low_reliability: z.string().catch(''),
        terms_and_conditions: z.string().catch(''),
        transporter_plate_registration: z.string().catch(''),
        state_motor_carrier_permit: z.string().catch(''),
        business_license: z.string().catch(''),
      })
      .nullable()
      .catch(null)
      .prefault({
        w9: '',
        usdot_certificate: '',
        cargo_insurance: '',
        low_reliability: '',
        terms_and_conditions: '',
        transporter_plate_registration: '',
        state_motor_carrier_permit: '',
        business_license: '',
      }),
    revoking_reasons: z
      .object({
        cargo_insurance_file_url: z.array(z.string().catch('')).catch([]),
        usdot_certificate_url: z.array(z.string().catch('')).catch([]),
        terms_and_conditions: z.array(z.string().catch('')).catch([]),
        fmcsa_phone: z.array(z.string().catch('')).catch([]),
      })
      .nullable()
      .catch(null)
      .prefault({
        cargo_insurance_file_url: [],
        usdot_certificate_url: [],
        terms_and_conditions: [],
        fmcsa_phone: [],
      }),
    non_verifiable_reasons: z
      .object({
        business_location_is_invalid: z.array(z.string().catch('')).catch([]),
        company_type_is_invalid: z.array(z.string().catch('')).catch([]),
      })
      .nullable()
      .catch(null)
      .prefault({
        business_location_is_invalid: [],
        company_type_is_invalid: [],
      }),
    reasons: z.object({
      w9: z.array(reasonSchema).catch([]),
      operation: z.array(reasonSchema).catch([]),
      insurance: z.array(reasonSchema).catch([]),
      transporter_plate_registration: z.array(reasonSchema).catch([]),
      state_motor_carrier_permit: z.array(reasonSchema).catch([]),
      business_license: z.array(reasonSchema).catch([]),
      terms_and_conditions: z.array(reasonSchema).catch([]),
      fmcsa_phone: z.array(reasonSchema).catch([]),
      reliability_score: z.array(reasonSchema).catch([]),
      company_type: z.array(reasonSchema).catch([]),
      business_location: z.array(reasonSchema).catch([]),
    }),
  }),
});

export const verifiedCarrierApplicationStatusYupSchema =
  verifiedCarrierApplicationStatusYupObjectSchema as unknown as Schema<VerifiedCarrierApplicationStatusDTO>;
